import {useEffect} from 'react';
import {useSelector} from 'react-redux';

import * as serviceWorkerMessageTypes from '../consts/serviceWorkerMessageTypes';
import configService from '../services/config/configService';
import localizationControllerService from '../services/localization/localizationControllerService';
import log from '../services/logger/log';
import notificationService from '../services/notificationService';
import {selectSwMessage} from '../state/selectors/global';
import {selectDusnByAssetId, selectDusnByCodentify} from '../state/selectors/iotDevices';
import {setLastReceivedMessageId} from '../state/slice/message';
import {dispatch, getState} from '../state/store';
import useOnPushNotificationClick from './useOnPushNotificationClick';

const useSWMessageHandler = () => {
    const swMessage = useSelector(selectSwMessage);
    const onPushNotificationClick = useOnPushNotificationClick();

    const handleMessage = async () => {
        const {type, data} = swMessage || {};

        switch (type) {
            case serviceWorkerMessageTypes.PUSH_NOTIFICATION_CLICK:
                onPushNotificationClick({...data, isNativeNotification: true});
                break;
            case serviceWorkerMessageTypes.SHOW_PUSH_NOTIFICATION:
                showPushNotification(data);
                break;
            case serviceWorkerMessageTypes.SERVICE_WORKER_ACTIVATED:
                onServiceWorkerActivated();
                break;
            default:
                break;
        }
    };

    const onServiceWorkerActivated = async () => {
        try {
            log.debug('useSWMessageHandler: Service worker activated');

            await configService.fetchGeneralConfigForSWCache();
            await localizationControllerService.fetchLocalizationForSWCache();
        } catch (e) {
            log.error('useSWMessageHandler: Error handling service worker activation', e.message);
        }
    };

    const showPushNotification = async (response) => {
        if (!response) return;

        const {title, body, data, icon} = response || {};
        const {
            codentify,
            assetId,
            campaignName,
            destinationType,
            notificationType,
            notificationSubType,
            timestamp,
            wasAppOpened,
            id,
        } = data || {};

        log.debug(`useSWMessageHandler: Received notification ${JSON.stringify(data)}`);

        dispatch(setLastReceivedMessageId(id));

        const dusn = selectDusnByCodentify(codentify)(getState()) || selectDusnByAssetId(assetId)(getState());

        notificationService.showPushNotification({
            titleText: title,
            messageText: body,
            iconSrc: icon,
            isExpired: true,
            wasAppOpened,
            dusn,
            codentify,
            assetId,
            destinationType,
            notificationType,
            notificationSubType,
            campaignName,
            timestamp,
            id,
        });
    };

    useEffect(() => {
        handleMessage();
    }, [swMessage]);
};

export default useSWMessageHandler;
